import { useEffect, useState } from 'react'

import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import { now as serverNow } from '~/models/clock'

/** 現在時刻の更新間隔 (ms)。 コマ (30 分) / 日付の境界跨ぎを 1 分以内に反映する。 */
const TICK_MS = 60_000

/**
 * 1 分ごとに更新される現在時刻 (app 横断の共有 hook)。
 *
 * 「現在時刻」 に依存する表示・判定を mount 時の `new Date()` で固定すると、 画面を
 * 開いたままコマ境界や日付をまたいだときに値が stale になる。 これを避けるため、
 * 現在時刻を state として持ち、 各計算の依存に含める。
 *
 * 消費者:
 * - 時間前市場 (`pages/IntradayBidding`): 受渡日から GC 状態を導く計算
 *   ([[gcBoundarySlot]] / [[gcLineSlot]] / [[nextGcForDeliveryDate]] /
 *   [[firstOpenSlot]]) — stale だと締切済コマがクリック可能なまま残る
 * - テロップバー (`components/layouts/MainLayout/TickerBar`): 日付・現在コマ表示
 *
 * 秒粒度が要るカウントダウン表示 (`GcCountdown`) は自前で 1 秒 tick を持つので、
 * ここは 1 分粒度に留めて再描画コストを抑える。
 *
 * tick は **壁時計の分境界に同期**させる (mount 時刻起点の `setInterval` にしない)。
 * GC は必ず分境界ちょうど (コマ開始 -1h) に来るため、 mount 起点だと GC を過ぎた
 * コマが最大 1 分間 `imminent` のまま表示され、 選択できるように見える
 * (codex 指摘 2026-08-06)。 送信時ガードは実時刻で判定するため送信自体は止まるが、
 * 表示と実際の可否がズレる時間帯が生まれる。 分境界に揃えることでこのズレを消す。
 */
export const useNowByMinute = (): Date => {
  const [now, setNow] = useState(() => serverNow())
  // offset は session RPC 完了時 (= 初回描画より後) に確定しうる。 確定時に値と
  // タイマー予約を取り直さないと、 偽装時刻・端末時計ずれ環境で最大 1 分ズレが残る。
  const offsetMs = useServerTimeOffsetMs()

  useEffect(() => {
    setNow(serverNow())
    let timer: ReturnType<typeof setTimeout>
    // epoch ms は分境界で 60_000 の倍数になるため、 剰余から次の分境界までの
    // 残り ms が求まる (= 0 にはならない。 ちょうど境界なら次の境界まで 1 分)。
    const msToNextMinute = (): number =>
      TICK_MS - (serverNow().getTime() % TICK_MS)
    const scheduleNext = (): void => {
      timer = setTimeout(() => {
        setNow(serverNow())
        scheduleNext()
      }, msToNextMinute())
    }
    scheduleNext()
    return () => {
      clearTimeout(timer)
    }
  }, [offsetMs])

  return now
}
